import type { ValueOf } from '../types';

type TelephoneFormatter = {
  request: (phoneNumber: string) => string;
  response: Record<TelephoneResponseFormatType, (phoneNumber: string) => string>;
};

export const TELEPHONE_COUNTRY_CODE = {
  KR: '82',
  UNSUPPORTED: 'unsupported',
} as const;

type TelephoneFormatOptions = {
  countryCode?: TelephoneCountryCode;
};

export type TelephoneResponseFormatType = 'input' | 'display';
export type TelephoneCountryCode = ValueOf<typeof TELEPHONE_COUNTRY_CODE>;
type SupportedTelephoneCountryCode = Exclude<
  TelephoneCountryCode,
  typeof TELEPHONE_COUNTRY_CODE.UNSUPPORTED
>;

const removePrefix = (phoneNumber: string, prefix: string) => {
  const formatted = phoneNumber.replace(/^\+/, '');
  return formatted.startsWith(prefix) ? formatted.slice(prefix.length) : formatted;
};

const normalizeKoreanNationalNumber = (phoneNumber: string) => {
  const nationalNumber = removePrefix(phoneNumber, TELEPHONE_COUNTRY_CODE.KR);
  return nationalNumber.startsWith('0') ? nationalNumber.slice(1) : nationalNumber;
};

const TELEPHONE_FORMATTERS = {
  [TELEPHONE_COUNTRY_CODE.KR]: {
    request: phoneNumber =>
      TELEPHONE_COUNTRY_CODE.KR.concat(normalizeKoreanNationalNumber(phoneNumber)),
    response: {
      input: phoneNumber => '0'.concat(normalizeKoreanNationalNumber(phoneNumber)),
      display: phoneNumber => {
        const nationalNumber = normalizeKoreanNationalNumber(phoneNumber);
        const formatted = nationalNumber.replace(/^([0-9*]{2})([0-9*]{4})([0-9*]{4})$/, '$1-$2-$3');
        return '+'.concat(TELEPHONE_COUNTRY_CODE.KR, '-', formatted);
      },
    },
  },
} satisfies Record<SupportedTelephoneCountryCode, TelephoneFormatter>;

const isSupportedTelephoneCountryCode = (
  countryCode: TelephoneCountryCode,
): countryCode is SupportedTelephoneCountryCode => countryCode in TELEPHONE_FORMATTERS;

const SUPPORTED_TELEPHONE_COUNTRY_CODES = Object.values(TELEPHONE_COUNTRY_CODE)
  .filter(isSupportedTelephoneCountryCode)
  .sort((a, b) => b.length - a.length);

const addPlusPrefix = (phoneNumber: string) =>
  phoneNumber.startsWith('+') ? phoneNumber : '+'.concat(phoneNumber);

const getTelephoneFormatter = (countryCode?: TelephoneCountryCode) =>
  countryCode && isSupportedTelephoneCountryCode(countryCode)
    ? TELEPHONE_FORMATTERS[countryCode]
    : undefined;

const getTelephoneFormatterFromServerFormat = (phoneNumber: string) => {
  const countryCode = getTelephoneCountryCodeFromServerFormat(phoneNumber);
  return getTelephoneFormatter(countryCode);
};

/**
 * 서버에서 받은 전화번호 형식에서 지원 중인 국가코드를 추출하는 함수
 * @description
 * - TELEPHONE_COUNTRY_CODE에 정의되어 있고 TELEPHONE_FORMATTERS에 등록된 국가코드만 분석합니다.
 * - 지원하지 않는 국가코드이거나 빈 값이면 TELEPHONE_COUNTRY_CODE.UNSUPPORTED를 반환합니다.
 * @example
 * // getTelephoneCountryCodeFromServerFormat('821012345678') => TELEPHONE_COUNTRY_CODE.KR
 * // getTelephoneCountryCodeFromServerFormat('+821012345678') => TELEPHONE_COUNTRY_CODE.KR
 * // getTelephoneCountryCodeFromServerFormat('111012345678') => TELEPHONE_COUNTRY_CODE.UNSUPPORTED
 */
export const getTelephoneCountryCodeFromServerFormat = (
  phoneNumber: string,
): TelephoneCountryCode => {
  if (!phoneNumber) return TELEPHONE_COUNTRY_CODE.UNSUPPORTED;

  const formatted = phoneNumber.replace(/^\+/, '');
  return (
    SUPPORTED_TELEPHONE_COUNTRY_CODES.find(countryCode => formatted.startsWith(countryCode)) ??
    TELEPHONE_COUNTRY_CODE.UNSUPPORTED
  );
};

/**
 * 클라이언트에서 서버로 전화번호를 전송할 때 사용하는 포맷팅 함수
 * @description
 * - countryCode를 명시하면 해당 국가 코드에 맞게 서버 전송 형식으로 변환합니다.
 * - countryCode를 명시하지 않으면 사용자 입력값을 그대로 반환합니다.
 * @example
 * // formatTelephoneForRequest('01012345678', { countryCode: TELEPHONE_COUNTRY_CODE.KR }) => '821012345678'
 * // formatTelephoneForRequest('1012345678', { countryCode: TELEPHONE_COUNTRY_CODE.KR }) => '821012345678'
 * // formatTelephoneForRequest('01012345678') => '01012345678'
 */
export const formatTelephoneForRequest = (
  phoneNumber: string,
  options: TelephoneFormatOptions = {},
): string | undefined => {
  if (!phoneNumber) return;

  const formatter = getTelephoneFormatter(options.countryCode);
  return formatter ? formatter.request(phoneNumber) : phoneNumber;
};

/**
 * 서버에서 받은 전화번호를 클라이언트에서 사용할 형식으로 변환하는 함수
 * @description
 * - countryCode를 명시하면 해당 국가 코드에 맞게 input 또는 display 형식으로 변환합니다.
 * - countryCode를 명시하지 않으면 서버 형식에서 지원 중인 국가코드를 자동으로 판단해 변환합니다.
 * - 지원하지 않는 국가코드이면 input은 그대로, display는 + prefix만 붙여 반환합니다.
 * @example
 * // formatTelephoneForResponse('821012345678', 'input', { countryCode: TELEPHONE_COUNTRY_CODE.KR }) => '01012345678'
 * // formatTelephoneForResponse('821012345678', 'display', { countryCode: TELEPHONE_COUNTRY_CODE.KR }) => '+82-10-1234-5678'
 * // formatTelephoneForResponse('821012345678', 'display') => '+82-10-1234-5678'
 */
export const formatTelephoneForResponse = (
  phoneNumber: string,
  formatType: TelephoneResponseFormatType,
  options: TelephoneFormatOptions = {},
): string | undefined => {
  if (!phoneNumber) return;

  const formatter = options.countryCode
    ? getTelephoneFormatter(options.countryCode)
    : getTelephoneFormatterFromServerFormat(phoneNumber);

  if (formatter) {
    return formatter.response[formatType](phoneNumber);
  }

  return formatType === 'display' ? addPlusPrefix(phoneNumber) : phoneNumber;
};
